import React, { useContext, useEffect, useMemo, useState } from "react";
import { Input, message, Modal, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { FacilityID } from "../../../../constants/defaultKeys";
import { UserContext } from "../../../../context/UserContext";
import { getCookie } from "../../../../helpers/localStorage";
import {
  getPractitionerListApi,
  updateIpServicesApi,
} from "../../../../services/receptionist.services";
import styles from "./PractitionerModal.module.css";

const PractitionerModal = ({
  open,
  setOpen,
  editData,
  refresh,
  selectedRowKeys,
  setSelectedRowKeys,
}) => {
  const { userDetails } = useContext(UserContext);
  const [data, setData] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const fn = () => setIsMobile(mq.matches);
    fn();
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setSearch("");
      return;
    }
    getPractitionerListApi(
      Number(getCookie(FacilityID)) || userDetails?.currFacility
    )
      .then((res) => {
        setData(res?.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [open, userDetails?.currFacility]);

  const practitioners = data?.data || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return practitioners;
    return practitioners.filter((item) => {
      const name = `${item?.first_name || ""} ${item?.last_name || ""}`.toLowerCase();
      const role = (item?.role || "").toLowerCase();
      return name.includes(q) || role.includes(q);
    });
  }, [practitioners, search]);

  const onFinish = () => {
    if (selectedId) {
      updateIpServicesApi({
        service_ids: selectedRowKeys,
        performed_by: selectedId.split("_")[0],
        status: "Done",
      })
        .then((res) => {
          message.success(res?.data?.message);
          setOpen(false);
          refresh();
          setSelectedRowKeys([]);
        })
        .catch((err) => {
          message.error(err?.response?.data?.message);
        });
    } else {
      message.error("Please select a practitioner");
    }
  };

  return (
    <Modal
      title="Choose practitioner"
      open={open}
      onCancel={() => setOpen(false)}
      onOk={onFinish}
      centered
      destroyOnClose
      width={isMobile ? "min(440px, calc(100vw - 32px))" : 480}
      wrapClassName={styles.practitionerModalWrap}
      okText="Assign"
      cancelText="Cancel"
    >
      <Input
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name or role"
        prefix={<SearchOutlined className={styles.searchIcon} />}
        className={styles.searchInput}
      />
      <div className={styles.practitionerList} role="listbox">
        {filtered.length === 0 ? (
          <div className={styles.emptyState}>No practitioners found</div>
        ) : (
          filtered.map((item) => {
            const id = `${item?.user_id}_${item?.org_user_id}`;
            const selected = selectedId === id;
            return (
              <button
                type="button"
                key={id}
                role="option"
                aria-selected={selected}
                className={`${styles.practitionerItem}${
                  selected ? ` ${styles.practitionerItemSelected}` : ""
                }`}
                onClick={() => setSelectedId(id)}
              >
                <span className={styles.practitionerName}>
                  {item?.first_name} {item?.last_name}
                </span>
                {item?.role ? (
                  <Tag className={styles.practitionerRole} title={item.role}>
                    {item.role}
                  </Tag>
                ) : null}
              </button>
            );
          })
        )}
      </div>
    </Modal>
  );
};

export default PractitionerModal;
